import type {
  DragEndEvent,
  DragStartEvent,
  DragOverEvent,
  DragCancelEvent,
} from '@dnd-kit/core'
import { Temporal } from '@js-temporal/polyfill'

import {
  format,
  endOfWeek,
  getZonedNow,
  isBefore,
  startOfWeek,
  getDateRange,
} from '~publish/helpers/temporal'
import type { WeekAnchor } from '~publish/helpers/temporal'

import type { CalendarBlockData, CalendarInfo, CalendarItem } from './types'

const FIVE_MINUTES_MS = 300000

const calendarInfoCache = new Map<string, CalendarInfo>()

const getDragTimestamp = (
  data: DragStartEvent['active']['data']['current'],
): number | null => {
  const timestamp = data?.timestamp
  return typeof timestamp === 'number' && Number.isInteger(timestamp)
    ? timestamp
    : null
}

const getDragLabel = (
  data: DragStartEvent['active']['data']['current'],
): string | null => {
  const label = data?.label
  return typeof label === 'string' && label.length > 0 ? label : null
}

/**
 * Efficiently calculates and caches calendar data (hours or days) based on input parameters.
 * This centralized calculation approach significantly improves performance by:
 * 1. Computing all date blocks at once rather than in child components
 * 2. Using a cache to avoid redundant calculations
 * 3. Organizing data in 2D arrays for more efficient rendering patterns
 *
 * Performance: <3ms for months and <7ms for weeks under standard conditions
 *
 * @param {Temporal.ZonedDateTime} selectedDate - The selected date to center the calendar around.
 * @param {'week' | 'month' | 'three-day'} viewMode - The current view mode of the calendar.
 * @param {0 | 1} weekStartsOn - The day to start the week on (0 for Sunday, 1 for Monday).
 * @param {Object} options - Configuration options.
 * @param {boolean} [options.enableCache] - Whether to use caching for performance optimization (defaults to `true`).
 * @returns {CalendarInfo} An object containing pre-structured arrays of hours and days data.
 */
export const calculateCalendarData = (
  selectedDate: Temporal.ZonedDateTime,
  viewMode: 'week' | 'month' | 'three-day',
  weekStartsOn: 0 | 1 = 0,
  {
    enableCache = true,
    weekAnchor = 'week-start',
  }: { enableCache?: boolean; weekAnchor?: WeekAnchor } = {},
): CalendarInfo => {
  if (!viewMode || !selectedDate) return { hours: [], days: [] }

  // Create a unique cache key based on the day, view mode, and week start setting
  // We use startOfDay to ensure time differences don't create unnecessary cache entries
  const key = `${selectedDate.year}-${selectedDate.month}-${selectedDate.day}-${selectedDate.timeZoneId}-${viewMode}-${weekStartsOn}-${weekAnchor}`

  // If we have cached data for this configuration, return it immediately
  // This is a major performance optimization that prevents recalculating the same data
  const cachedCalendarInfo = calendarInfoCache.get(key)
  if (enableCache && cachedCalendarInfo) {
    return cachedCalendarInfo
  }

  // Calculate different data structures based on view mode
  let hours: CalendarBlockData[][] = []
  let days: CalendarBlockData[][] = []

  if (viewMode === 'week' || viewMode === 'three-day') {
    const numberOfDays = viewMode === 'three-day' ? 3 : 7
    hours = calculateCalendarHours(
      selectedDate,
      weekStartsOn,
      numberOfDays,
      weekAnchor,
    )
  } else {
    days = calculateCalendarDays(selectedDate, weekStartsOn)
  }
  const result = {
    hours,
    days,
  }
  if (enableCache) {
    calendarInfoCache.set(key, result)
  }

  return result
}

/**
 * Efficiently calculates hour blocks for a week view of the calendar.
 * Groups hours by hour of day (0-23) for easier rendering in a grid layout.
 * Handles DST transitions (both skipped and repeated hours) correctly.
 *
 * The resulting 2D array structure optimizes rendering by grouping the same hour
 * across different days, reducing additional data transformations in components.
 *
 * @param {Temporal.ZonedDateTime} selectedDate - The selected date to center the week around.
 * @param {0 | 1} weekStartsOn - The day to start the week on (0 for Sunday, 1 for Monday).
 * @returns {CalendarBlockData[][]} A 2D array of hour blocks grouped by hour of day.
 */
function calculateCalendarHours(
  currentDate: Temporal.ZonedDateTime,
  weekStartsOn: 0 | 1,
  numberOfDays = 7,
  weekAnchor: WeekAnchor = 'week-start',
): CalendarBlockData[][] {
  const startsPreviousDay = numberOfDays === 7 && weekAnchor === 'previous-day'
  // Get the start and end of the date range
  const startZdt = startsPreviousDay
    ? currentDate.subtract({ days: 1 }).startOfDay()
    : numberOfDays === 7
      ? startOfWeek(currentDate, { weekStartsOn })
      : currentDate.startOfDay()
  const endZdt = startsPreviousDay
    ? startZdt.add({ days: 7 }).startOfDay()
    : numberOfDays === 7
      ? endOfWeek(currentDate, { weekStartsOn })
      : currentDate.add({ days: numberOfDays }).startOfDay()
  const timeZone = currentDate.timeZoneId

  const startEpoch = startZdt.toInstant().epochMilliseconds
  const endEpoch = endZdt.toInstant().epochMilliseconds

  // Get current time in the specified timezone for determining creation timestamps
  const nowTimestamp = getZonedNow(timeZone)
    .round('minute')
    .toInstant().epochMilliseconds

  // Create a 2D array to store hours grouped by hour of day (0-23)
  // This structure makes it easier to render hours in a grid layout
  // Each inner array will contain the same hour across different days
  // This 2D structure eliminates the need for additional transformations in the Week component
  const hoursByHourOfDay: CalendarBlockData[][] = Array.from(
    { length: 24 },
    () => [],
  )

  // Step in 1-hour increments from startEpoch to endEpoch (but watch for DST skip / repeat)
  let currentMs = startEpoch
  let prevZdt =
    Temporal.Instant.fromEpochMilliseconds(currentMs).toZonedDateTimeISO(
      timeZone,
    )

  while (currentMs < endEpoch) {
    // Move forward one hour numerically
    let nextMs = currentMs + 60 * 60 * 1000

    let nextZdt =
      Temporal.Instant.fromEpochMilliseconds(nextMs).toZonedDateTimeISO(
        timeZone,
      )

    const isSameDay = nextZdt.day === prevZdt.day
    const hourDiff = nextZdt.hour - prevZdt.hour

    // Handle DST transitions:
    // 1. When a timezone skips an hour (e.g., spring forward), hourDiff will be > 1
    // 2. When a timezone repeats an hour (e.g., fall back), hourDiff will be 0
    // NOTE: this will not work with Lord Howe Island (Australia) because it
    // has a DST change of 30 minutes

    // If the hour is skipped in the timezone, we add a placeholder hour block for it
    // This ensures the UI still shows all 24 hours even during DST transitions
    if (hourDiff > 1 && isSameDay) {
      hoursByHourOfDay[prevZdt.hour + 1].push({
        // Nothing should be scheduled in this hour so we set the start and
        // end to the same timestamp
        startTimestamp: nextMs - 1,
        endTimestamp: nextMs - 1,
        creationTimestamp: nextMs - 1 + FIVE_MINUTES_MS,
        start: prevZdt,
        isDSTSkippedHour: true,
      })
    }

    // If local hour didn't change, we hit a DST repetition, so we add one more hour
    // This handles "fall back" where an hour is repeated
    if (hourDiff === 0 && isSameDay) {
      nextMs += 60 * 60 * 1000
      nextZdt =
        Temporal.Instant.fromEpochMilliseconds(nextMs).toZonedDateTimeISO(
          timeZone,
        )
    }

    const startTimestamp = currentMs
    const endTimestamp = nextMs - 1

    // Calculate creation timestamp for new events:
    // - If the hour is in the future, use the start of the hour
    // - If the hour is in the past or current, use current time + 5 minutes
    const creationTimestamp =
      startTimestamp > nowTimestamp
        ? startTimestamp
        : nowTimestamp + FIVE_MINUTES_MS

    // Add the hour block to the appropriate hour group
    // This organizes hours by their hour-of-day value (0-23)
    hoursByHourOfDay[prevZdt.hour].push({
      startTimestamp,
      endTimestamp,
      creationTimestamp,
      start: prevZdt,
    })

    // Advance to the next hour
    currentMs = nextMs
    if (currentMs < endEpoch) {
      prevZdt = nextZdt
    }
  }

  return hoursByHourOfDay
}

/**
 * Efficiently calculates day blocks for a month view of the calendar.
 * Groups days by week for optimized grid rendering, minimizing Temporal object creation.
 *
 * The resulting 2D array structure (days grouped by week) eliminates the need for
 * additional data transformations in the Month component, improving rendering performance.
 *
 * @param {Temporal.ZonedDateTime} selectedDate - The selected date to center the month around.
 * @param {0 | 1} weekStartsOn - The day to start the week on (0 for Sunday, 1 for Monday).
 * @returns {CalendarBlockData[][]} A 2D array of day blocks grouped by week.
 */
function calculateCalendarDays(
  selectedDate: Temporal.ZonedDateTime,
  weekStartsOn: 0 | 1,
): CalendarBlockData[][] {
  const { start, end } = getDateRange(selectedDate, 'month', weekStartsOn)
  const timeZone = selectedDate.timeZoneId

  // Calculate number of weeks in the month view
  // This complex calculation handles edge cases:
  // 1. When the month spans across year boundaries
  // 2. Adjusts for different week start days (Sunday or Monday)
  // 3. Handles years with 53 weeks.A year has 53 weeks when:
  //    - January 1st falls on a Thursday (for regular years)
  //    - January 1st falls on a Wednesday or Thursday (for leap years)

  // We use modulo 53 to handle year boundary cases: when crossing years, or when the year has 53 weeks.
  // (end.weekOfYear - start.weekOfYear) will be negative.
  // Adding 53 ensures the result is positive, then modulo 53 gives us the correct number of weeks.

  // weekOfYear is typed as optional (undefined only for non-ISO calendars),
  // so fall back to 0; these ZonedDateTimes always use the ISO calendar.
  const endWeekOfYear = end.weekOfYear ?? 0
  const startWeekOfYear = start.weekOfYear ?? 0
  const numberOfWeeks =
    ((endWeekOfYear - startWeekOfYear + 53) % 53) + weekStartsOn

  let dayZdt = start.startOfDay()
  const endZdt = end.add({ days: 1 }).startOfDay()

  // Get current time in the specified timezone for determining creation timestamps
  const nowTimestamp = getZonedNow(timeZone)
    .round('minute')
    .toInstant().epochMilliseconds

  // Create a 2D array to store days grouped by week
  // This structure makes it easier to render days in a grid layout
  // Each inner array represents a week containing up to 7 days
  const daysByWeek: CalendarBlockData[][] = Array.from(
    { length: numberOfWeeks },
    () => [],
  )
  let weekIndex = 0

  // Process each day from start to end date
  while (isBefore(dayZdt, endZdt)) {
    const nextDayZdt = dayZdt.add({ days: 1 })

    const startTimestamp = dayZdt.toInstant().epochMilliseconds
    const endTimestamp = nextDayZdt.toInstant().epochMilliseconds - 1

    // Use noon as the default creation time for new events
    // This places new events in the middle of the day by default
    const noonMs = startTimestamp + 12 * 60 * 60 * 1000

    // Calculate creation timestamp for new events:
    // - If the day is in the future, use noon of that day
    // - If the day is in the past or current, use current time + 5 minutes
    const creationTimestamp =
      noonMs > nowTimestamp ? noonMs : nowTimestamp + FIVE_MINUTES_MS

    const day: CalendarBlockData = {
      startTimestamp,
      endTimestamp,
      creationTimestamp,
      start: dayZdt,
    }

    // Push into the current week array
    daysByWeek[weekIndex].push(day)

    // If we have 7 days in this row, advance to the next week
    if (daysByWeek[weekIndex].length === 7) {
      weekIndex++
      if (weekIndex >= numberOfWeeks) break
    }

    dayZdt = nextDayZdt
  }

  return daysByWeek
}

/**
 * Generates announcement functions for drag and drop operations in a the calendar.
 *
 * @param {Object} options - The options for generating announcements.
 * @param {string} options.timezone - The timezone to use for formatting dates and times.
 * @param {boolean} options.is24HourFormat - Whether to use 24-hour time format.
 * @param {'week' | 'month'} options.viewMode - The current view mode of the calendar.
 * @param {CalendarItem[]} [options.items] - An array of calendar items (defaults to an empty array).
 *
 * @returns {Object} An object containing announcement functions for different drag and drop events.
 * @property {function} onDragStart - Announces when an item starts being dragged.
 * @property {function} onDragOver - Announces when a dragged item is over a droppable area.
 * @property {function} onDragEnd - Announces when a drag operation ends.
 * @property {function} onDragCancel - Announces when a drag operation is cancelled.
 */
export function getDndKitAnnouncements({
  timezone,
  is24HourFormat,
  viewMode,
  items = [],
}: {
  timezone: string
  is24HourFormat: boolean
  viewMode: 'week' | 'month' | 'three-day'
  items: CalendarItem[] | undefined
}): {
  onDragStart: (event: DragStartEvent) => string
  onDragOver: (event: DragOverEvent) => string
  onDragEnd: (event: DragEndEvent) => string
  onDragCancel: (event: DragCancelEvent) => string
} {
  const dateTimeFormatter = (timestamp: number): string => {
    const zonedDate =
      Temporal.Instant.fromEpochMilliseconds(timestamp).toZonedDateTimeISO(
        timezone,
      )
    return format(
      zonedDate,
      `eee, MMMM d 'at' ${is24HourFormat ? 'HH:mm' : 'h:mm a'}`,
    )
  }
  const dateFormatter = (timestamp: number): string => {
    const zonedDate =
      Temporal.Instant.fromEpochMilliseconds(timestamp).toZonedDateTimeISO(
        timezone,
      )
    return format(zonedDate, 'eee, MMMM d')
  }
  const dropTargetLabel = (
    data: DragStartEvent['active']['data']['current'],
  ): string => {
    const timestamp = getDragTimestamp(data)
    const label = getDragLabel(data)
    if (timestamp === null) return label ?? 'a droppable area'

    return viewMode === 'month'
      ? dateFormatter(timestamp)
      : dateTimeFormatter(timestamp)
  }
  const activeItemLabel = (active: DragStartEvent['active']): string => {
    const calendarItem = items.find((item) => item.id === active.id.toString())
    if (calendarItem) {
      return `item scheduled for ${dateTimeFormatter(calendarItem.timestamp)}`
    }

    return getDragLabel(active.data.current) ?? 'item'
  }

  return {
    onDragStart({ active }: DragStartEvent): string {
      return `Picked up ${activeItemLabel(active)}.`
    },
    onDragOver({ active, over }: DragOverEvent): string {
      if (!over?.id) return ``

      if (over) {
        return `${activeItemLabel(active)} is over ${dropTargetLabel(
          over.data.current,
        )}.`
      }
      return `Item is no longer over a droppable area.`
    },
    onDragEnd({ active, over }: DragEndEvent): string {
      const activeTimestamp = getDragTimestamp(active.data.current)
      const overTimestamp = getDragTimestamp(over?.data.current)

      if (
        activeTimestamp !== null &&
        overTimestamp !== null &&
        overTimestamp === activeTimestamp
      ) {
        return `${activeItemLabel(active)} was moved to its original position.`
      }

      if (over) {
        if (overTimestamp !== null) {
          if (activeTimestamp === null) {
            return `${activeItemLabel(active)} was scheduled for ${dropTargetLabel(
              over.data.current,
            )}.`
          }

          return `${activeItemLabel(active)} was rescheduled to ${dropTargetLabel(
            over.data.current,
          )}.`
        }

        return `${activeItemLabel(active)} was dropped on ${dropTargetLabel(
          over.data.current,
        )}.`
      }
      return `Item was dropped back in its original position.`
    },
    onDragCancel({ active }: DragCancelEvent): string {
      return `Dragging was cancelled. ${activeItemLabel(
        active,
      )} was returned to its original position.`
    },
  }
}
